import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { compatApi } from '../../utils/Intercepter';
import VacationDetailModal from './VacationDetailModal';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';
import { useIsMobile } from '../../utils/responsive';


const STATUS_STYLE = {
    PENDING: { label: '검토 중', color: '#5d8fe0', bg: 'rgba(93,143,224,.12)', dot: '#5d8fe0' },
    APPROVED: { label: '승인', color: MINT, bg: hexToRgba(MINT, .14), dot: MINT },
    REJECTED: { label: '반려', color: '#c44569', bg: 'rgba(196,69,105,.12)', dot: '#c44569' },
    DRAFT: { label: '임시저장', color: '#8b8a99', bg: '#f4f3f9', dot: '#aaa9b7' },
};

function StatusBadge({ status }) {
    const s = STATUS_STYLE[status] || { label: status, color: '#8b8a99', bg: '#f4f3f9', dot: '#aaa9b7' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 11.5, fontWeight: 600, color: s.color, background: s.bg,
            padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
        }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />
            {s.label}
        </span>
    );
}

function TypeChip({ type }) {
    const map = {
        'VACATION': { label: '휴가', color: MINT, bg: hexToRgba(MINT, .14) },
        '휴가 신청서': { label: '휴가', color: MINT, bg: hexToRgba(MINT, .14) },
    };
    const s = map[type] || { label: type, color: '#5d5b6e', bg: '#f4f3f9' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center',
            fontSize: 11, fontWeight: 600, color: s.color, background: s.bg,
            padding: '3px 8px', borderRadius: 6, lineHeight: 1.45, whiteSpace: 'nowrap',
        }}>{s.label}</span>
    );
}

function EAStat({ label, value, suffix, accent, icon, highlight }) {
    return (
        <div style={{
            background: 'white', borderRadius: 16, padding: '16px 18px',
            border: highlight ? `1.5px solid ${hexToRgba(accent, .35)}` : '1px solid #f0eff5',
            boxShadow: '0 1px 2px rgba(60,50,120,.04)',
            display: 'flex', alignItems: 'center', gap: 14,
        }}>
            <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: hexToRgba(accent, .12), color: accent,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>{icon}</div>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <span style={{ fontSize: 11.5, color: '#8b8a99', fontWeight: 500 }}>{label}</span>
                <span style={{ fontSize: 22, fontWeight: 700, color: '#1f1d2e', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                    {value}<span style={{ fontSize: 12, color: '#8b8a99', fontWeight: 500, marginLeft: 3 }}>{suffix}</span>
                </span>
            </div>
        </div>
    );
}

export default function ApprovalInboxPage() {
    const isMobile = useIsMobile(768);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterFrom, setFilterFrom] = useState('');
    const [filterTo, setFilterTo] = useState('');
    const [filterTab, setFilterTab] = useState('전체');

    const fetchItems = useCallback(() => {
        setLoading(true);
        compatApi.get('/approval/inbox')
            .then(res => setItems(res.data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchItems(); }, [fetchItems]);

    const handleReset = () => {
        setSearchText(''); setFilterStatus(''); setFilterFrom(''); setFilterTo('');
    };

    const filteredItems = useMemo(() => {
        const keyword = searchText.trim().toLowerCase();
        return items.filter(item => {
            if (filterStatus && item.status !== filterStatus) return false;
            if (filterFrom && item.submittedAt && item.submittedAt.substring(0, 10) < filterFrom) return false;
            if (filterTo && item.submittedAt && item.submittedAt.substring(0, 10) > filterTo) return false;
            if (keyword) {
                const t = (item.title || '').toLowerCase();
                const d = (item.drafterName || '').toLowerCase();
                const o = (item.organizationName || '').toLowerCase();
                if (!t.includes(keyword) && !d.includes(keyword) && !o.includes(keyword)) return false;
            }
            return true;
        });
    }, [items, searchText, filterStatus, filterFrom, filterTo]);

    const statCounts = useMemo(() => {
        const thisYear = String(new Date().getFullYear());
        const yearItems = items.filter(i => i.submittedAt?.startsWith(thisYear));
        return {
            pending: yearItems.filter(i => i.status === 'PENDING').length,
            approved: yearItems.filter(i => i.status === 'APPROVED').length,
            rejected: yearItems.filter(i => i.status === 'REJECTED').length,
        };
    }, [items]);

    const tabFilteredItems = useMemo(() => {
        const tabMap = { '검토 중': 'PENDING', '승인': 'APPROVED', '반려': 'REJECTED' };
        const statusVal = tabMap[filterTab];
        return statusVal ? filteredItems.filter(i => i.status === statusVal) : filteredItems;
    }, [filteredItems, filterTab]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 61px)', background: '#f5f4fa' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${hexToRgba(PURPLE, 0.2)}`, borderTopColor: PURPLE, animation: 'spin 0.7s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    return (
        <>
            <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
                <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>

                    {/* Page header */}
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
                            <span>전자결재</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(-90deg)' }}><path d="M6 9l6 6 6-6" /></svg>
                            <span style={{ color: '#3a3849', fontWeight: 500 }}>결재함</span>
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>결재함</h1>
                            <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#7a7989' }}>내게 도착한 결재 요청을 검토하고 승인 또는 반려합니다.</p>
                        </div>
                    </div>

                    {/* Stat tiles */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: 14, marginBottom: 16 }}>
                        <EAStat
                            label="대기" value={statCounts.pending} suffix="건" accent={PURPLE}
                            highlight={statCounts.pending > 0}
                            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>}
                        />
                        <EAStat
                            label="승인 완료" value={statCounts.approved} suffix="건" accent={MINT}
                            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>}
                        />
                        <EAStat
                            label="반려" value={statCounts.rejected} suffix="건" accent="#c44569"
                            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M9 9l6 6M15 9l-6 6" /></svg>}
                        />
                    </div>

                    {/* Filter row */}
                    <div style={{
                        background: 'white', borderRadius: 16, padding: '14px 20px',
                        border: '1px solid #f0eff5', boxShadow: '0 1px 2px rgba(60,50,120,.04)',
                        marginBottom: 16,
                        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    }}>
                        <input
                            type="text"
                            placeholder="제목, 기안자, 조직으로 검색"
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            style={{ height: 36, borderRadius: 8, border: '1.5px solid #ececf2', background: '#fafaff', padding: '0 12px', fontSize: 13, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', width: 260, boxSizing: 'border-box' }}
                            onFocus={e => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 3px ${hexToRgba(PURPLE, 0.1)}`; }}
                            onBlur={e => { e.target.style.borderColor = '#ececf2'; e.target.style.boxShadow = 'none'; }}
                        />
                        <input
                            type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
                            title="제출일 시작"
                            style={{ height: 36, borderRadius: 8, border: '1.5px solid #ececf2', background: '#fafaff', padding: '0 12px', fontSize: 13, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', width: 148 }}
                        />
                        <span style={{ color: '#c7c5d2', fontSize: 13 }}>~</span>
                        <input
                            type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
                            title="제출일 종료"
                            style={{ height: 36, borderRadius: 8, border: '1.5px solid #ececf2', background: '#fafaff', padding: '0 12px', fontSize: 13, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit', width: 148 }}
                        />
                        <button onClick={handleReset} style={{
                            height: 36, padding: '0 14px', borderRadius: 8,
                            border: '1.5px solid #ececf2', background: 'white',
                            fontSize: 13, color: '#5d5b6e', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                        }}>초기화</button>
                    </div>

                    {/* Main card */}
                    <div style={{
                        background: 'white', borderRadius: 16,
                        border: '1px solid #f0eff5',
                        boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                        overflow: 'hidden',
                    }}>
                        {/* Card header with tab filter + actions */}
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '14px 20px',
                            background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)',
                            borderBottom: '1px solid #ececf2',
                            gap: 12, flexWrap: 'wrap',
                        }}>
                            <div style={{ display: 'inline-flex', gap: 4, background: 'white', padding: 3, borderRadius: 10, border: '1px solid #ececf2' }}>
                                {['전체', '검토 중', '승인', '반려'].map(opt => {
                                    const active = filterTab === opt;
                                    return (
                                        <button
                                            key={opt}
                                            onClick={() => setFilterTab(opt)}
                                            style={{
                                                height: 28, padding: '0 12px', borderRadius: 7, border: 'none',
                                                background: active ? `linear-gradient(135deg, ${PURPLE}, ${MINT})` : 'transparent',
                                                color: active ? 'white' : '#5d5b6e',
                                                fontSize: 12, fontWeight: active ? 600 : 500,
                                                cursor: 'pointer', fontFamily: 'inherit', transition: 'all 140ms',
                                            }}
                                        >{opt}</button>
                                    );
                                })}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <span style={{ fontSize: 12, color: '#8b8a99' }}>총 {tabFilteredItems.length}건</span>
                                <button onClick={fetchItems} disabled={loading} style={{
                                    height: 32, padding: '0 14px', borderRadius: 8,
                                    border: `1.5px solid ${MINT}`, background: 'white',
                                    color: MINT, fontSize: 12.5, fontWeight: 600,
                                    cursor: 'pointer', fontFamily: 'inherit',
                                }}>↻ 새로 고침</button>
                            </div>
                        </div>

                        {/* Table */}
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 900 }}>
                                <colgroup>
                                    <col style={{ width: 150 }} /><col style={{ width: 80 }} /><col />
                                    <col style={{ width: 120 }} /><col style={{ width: 120 }} /><col style={{ width: 120 }} /><col style={{ width: 120 }} />
                                </colgroup>
                                <thead>
                                    <tr style={{ background: '#f7f6fb' }}>
                                        {['문서 번호', '구분', '제목', '기안자', '제출일', '조직', '상태'].map((h, i) => (
                                            <th key={h} style={{
                                                padding: '12px 14px', fontSize: 12, fontWeight: 600, color: '#5d5b6e',
                                                textAlign: i === 2 ? 'left' : 'center',
                                                borderBottom: '1px solid #ececf2', whiteSpace: 'nowrap', letterSpacing: '.02em',
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tabFilteredItems.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} style={{ padding: '48px 20px', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>
                                                {items.length === 0 ? '결재 대기 중인 문서가 없습니다.' : '검색 결과가 없습니다.'}
                                            </td>
                                        </tr>
                                    ) : tabFilteredItems.map((item, i) => (
                                        <tr
                                            key={item.id || i}
                                            style={{ cursor: 'pointer', background: 'white', transition: 'background 100ms' }}
                                            onClick={() => setSelectedId(item.id)}
                                            onMouseEnter={e => e.currentTarget.style.background = '#fafaff'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'white'}
                                        >
                                            <td style={{ padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid #f4f3f9', whiteSpace: 'nowrap' }}>
                                                <span style={{ fontSize: 11.5, color: '#8b8a99', fontWeight: 600, background: '#f4f3f9', padding: '2px 8px', borderRadius: 6, fontVariantNumeric: 'tabular-nums' }}>{item.id || String(i + 1)}</span>
                                            </td>
                                            <td style={{ padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid #f4f3f9', whiteSpace: 'nowrap' }}>
                                                <TypeChip type={item.docType} />
                                            </td>
                                            <td style={{ padding: '12px 14px', textAlign: 'left', borderBottom: '1px solid #f4f3f9', fontSize: 13, color: '#1f1d2e', fontWeight: 500 }}>{item.title}</td>
                                            <td style={{ padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid #f4f3f9', fontSize: 13, color: '#3a3849', whiteSpace: 'nowrap' }}>{item.drafterName || '-'}</td>
                                            <td style={{ padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid #f4f3f9', fontSize: 13, color: '#3a3849', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{item.submittedAt?.substring(0, 10) || '-'}</td>
                                            <td style={{ padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid #f4f3f9', fontSize: 13, color: '#3a3849', whiteSpace: 'nowrap' }}>{item.organizationName || '-'}</td>
                                            <td style={{ padding: '12px 14px', textAlign: 'center', borderBottom: '1px solid #f4f3f9', whiteSpace: 'nowrap' }}><StatusBadge status={item.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {tabFilteredItems.length > 0 && (
                            <div style={{ padding: '12px 20px', borderTop: '1px solid #ececf2', background: '#fafaff', fontSize: 12, color: '#8b8a99' }}>
                                {tabFilteredItems.length}건 / 전체 {items.length}건
                            </div>
                        )}
                    </div>
                </main>
            </div>

            <VacationDetailModal
                show={!!selectedId}
                onHide={() => setSelectedId(null)}
                vacationId={selectedId}
                mode="inbox"
                onAction={fetchItems}
            />
        </>
    );
}
